import React, { useState } from 'react';
import type { FC } from 'react';
import { useParams } from 'react-router';

import { ApolloError } from '@apollo/client';

import { Text } from '@shared/components/bootstrap';
import { Panel } from '@shared/components/bootstrap/panel';

import { useNavigate } from '@admin/hooks/use_navigate';
import { usePermission } from '@admin/hooks/use_permission';
import { useTitle } from '@admin/hooks/use_title';
import { VEHICLE, VEHICLE_STATUS } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import { Status, useVehicleDetailsQuery, useVehicleUpdateMutation, type Vehicle__Input } from '@admin/schema';

import { VehicleForm } from './form';

const UNAUTHORIZED_SAVE_MESSAGE = 'You are not authorized to save these changes.';

export const VehicleEdit: FC = () => {
  useTitle('Edit Vehicle');
  const { id } = useParams<{ id: string }>();
  const canUpdateDetails = usePermission(VEHICLE.resource, VEHICLE.actions.update);
  const canUpdateStatus = usePermission(VEHICLE_STATUS.resource, VEHICLE_STATUS.actions.update);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [execute, { loading, data }] = useVehicleUpdateMutation({ client });
  const { data: details } = useVehicleDetailsQuery({ client, variables: { id } });
  const navigate = useNavigate();
  if (!details) return null;

  if (!canUpdateDetails && !canUpdateStatus) {
    return (
      <Panel>
        <Panel.Body>
          <Text tag="p" style="muted">
            You do not have permission to edit this vehicle. If you believe this is an error please contact your
            supervisor.
          </Text>
        </Panel.Body>
      </Panel>
    );
  }

  const defaults: Partial<Vehicle__Input> = {
    comments: details.vehicle.comments,
    grossWeightRating: details.vehicle.grossWeightRating,
    inspectionExpiresOn: details.vehicle.inspectionExpiresOn,
    irmNumber: details.vehicle.irmNumber,
    licensePlate: details.vehicle.licensePlate,
    maintenanceDueOn: details.vehicle.maintenanceDueOn,
    originState: details.vehicle.originState,
    overwrittenCuft: details.vehicle.overwrittenCuft,
    overwrittenNumSeats: details.vehicle.overwrittenNumSeats,
    ownershipKind: details.vehicle.ownershipKind,
    providerUnitNumber: details.vehicle.providerUnitNumber,
    regionID: details.vehicle.region.id,
    registrationExpiresOn: details.vehicle.registrationExpiresOn,
    serialCode: details.vehicle.serialCode,
    status: details.vehicle.status,
    tollTagNumber: details.vehicle.tollTagNumber,
    typeID: details.vehicle.vehicleType?.id ?? undefined,
    vin: details.vehicle.vin,
    warehouseID: details.vehicle.warehouse.id,
  };

  const save = async (input: Vehicle__Input) => {
    setSaveError(null);
    let response;
    try {
      response = await execute({ variables: { id, input } });
    } catch (e: unknown) {
      const message =
        e instanceof ApolloError
          ? e.graphQLErrors.map((err) => err.message).join(' ') || e.message
          : e instanceof Error
            ? e.message
            : UNAUTHORIZED_SAVE_MESSAGE;
      setSaveError(message || UNAUTHORIZED_SAVE_MESSAGE);
      return;
    }
    if (response.errors?.length) {
      setSaveError(response.errors[0]?.message ?? UNAUTHORIZED_SAVE_MESSAGE);
      return;
    }
    if (response?.data?.result?.status !== Status.Ok) return;
    const vehicle = response?.data?.result?.vehicle;
    if (!vehicle) return;
    navigate(`/vehicles/${vehicle.id}`);
  };

  const error = saveError ?? data?.result?.error;

  return (
    <VehicleForm
      save={save}
      loading={loading}
      defaults={defaults}
      error={error}
      canEditDetails={canUpdateDetails}
      canEditStatus={canUpdateStatus}
    />
  );
};
